import { inject } from 'mobx-react';
import { observe } from 'mobx';
import * as React from 'react';

import { ContextStore } from 'bernie-core';

import {
  DateField,
  DateFieldData,
  FormInputs,
  FormInputsProvider,
  FormQueryVariables,
  LodgingFormEvent,
  SubmitButton,
  SubmitButtonData,
  TravelersData,
  TravelersField,
  TravelersFieldTriggerType,
} from '@shared-ui/lodging-form';
import { useBexApi } from '@shared-ui/bex-api-context';
import { useLocalization } from '@shared-ui/localization-context';
import { useRefContext } from '@shared-ui/ref-context';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { getDateRangeInput, getRooms } from 'stores/adapters';
import { SearchCriteriaModel } from 'stores/models';

import { ActionProviderDatedMobile } from './action-provider';

export const START_DATE_REF_NAME = 'startDate';
export const END_DATE_REF_NAME = 'endDate';

export const SharedUiDateFieldWrapper: React.FC<{
  buttonText: string;
  closeOnSubmit?: boolean;
  onDismiss?: () => void;
  invalidDatesText?: string;
  currentHotel?: CurrentHotelStore;
  context?: ContextStore;
}> = inject(
  'context',
  'currentHotel'
)(({ context, currentHotel, buttonText, onDismiss, closeOnSubmit = true, invalidDatesText }) => {
  const { registerTarget } = useRefContext();

  // Register targets so that datepicker can be opened from different parts of the page
  const startDateRef = registerTarget<HTMLDivElement>(START_DATE_REF_NAME);
  const endDateRef = registerTarget<HTMLDivElement>(END_DATE_REF_NAME);

  const [placeholder, setPlaceholder] = React.useState(context?.localDateFormat);
  const { formatText } = useLocalization();

  React.useEffect(() => {
    setPlaceholder(formatText('hotels.search.form.toDate.placeholder'));
  }, []);

  // TODO update this logic to use react context
  React.useEffect(() => {
    /* istanbul ignore next */
    const disposer = observe(currentHotel, 'openNewDatePicker', change => {
      if (change.newValue) {
        startDateRef?.current?.click();
        currentHotel.setOpenNewDatePicker(false);
      }
    });

    return disposer;
  }, []);

  const dateFieldData: DateFieldData = {
    startId: 'hotels-check-in',
    startLabel: formatText('hotels.search.form.fromDateHyphen.label'),
    startName: 'startDate',
    startPlaceholder: placeholder,
    startInvalid: '',
    endId: 'hotels-check-out',
    endLabel: formatText('hotels.search.form.toDateHyphen.label'),
    endName: 'endDate',
    endPlaceholder: placeholder,
    endInvalid: '',
    buttonText,
    daysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
    midnightBookingOffset: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
    __typename: 'LodgingFormDateField',
    validations: [
      {
        __typename: 'StartDateRequiredValidation',
        error: invalidDatesText || formatText('hotels.search.validation.invalidFromDate.text'),
        events: [LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT],
      },
      {
        __typename: 'EndDateRequiredValidation',
        error: invalidDatesText || formatText('hotels.search.validation.invalidToDate.text'),
        events: [LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT],
      },
      {
        __typename: 'DateOrderValidation',
        error: formatText('hotels.search.validation.invalidDateRange.text'),
        events: [LodgingFormEvent.ON_LOAD, LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT],
      },
      {
        __typename: 'MaxDateRangeValidation',
        error: formatText('hotels.search.validation.invalidDateDateRangeLength.text'),
        maxDateRange: 28,
        events: [LodgingFormEvent.ON_LOAD, LodgingFormEvent.ON_CHANGE, LodgingFormEvent.ON_SUBMIT],
      },
    ],
  };

  return (
    <Viewport>
      <ViewSmall>
        <DateField
          fieldName="dates"
          data={dateFieldData}
          closeOnSubmit={closeOnSubmit}
          onDismiss={onDismiss}
          isOpenOverride={true}
          startDateRef={startDateRef}
          endDateRef={endDateRef}
        />
      </ViewSmall>
      <ViewLarge>
        <>
          <DateField
            fieldName="dates"
            data={dateFieldData}
            closeOnSubmit={closeOnSubmit}
            startDateRef={startDateRef}
            endDateRef={endDateRef}
          />
        </>
      </ViewLarge>
    </Viewport>
  );
});

export const SharedUiTravelersWrapper: React.FC<{ doneLabel?: string; closeOnSubmit?: boolean; trigger?: TravelersFieldTriggerType }> = ({
  doneLabel,
  closeOnSubmit = true,
  trigger = TravelersFieldTriggerType.INPUT,
}) => {
  const { formatText } = useLocalization();

  const travelersData: TravelersData = {
    __typename: 'LodgingFormTravelers',
    label: formatText('hotels.search.form.travelers.label'),
    travelersSummary: {
      __typename: 'TravelersSummary',
      singleRoomSingleGuest: formatText('hotels.roomPicker.roomSummary.singleRoomSingleGuest'),
      singleRoomMultipleGuests: formatText('hotels.roomPicker.roomSummary.singleRoomMultipleGuests', '{0}'),
      multipleRoomsSingleGuest: formatText('hotels.roomPicker.roomSummary.multipleRoomsSingleGuest', '{0}'),
      multipleRoomsMultipleGuests: formatText('hotels.roomPicker.roomSummary.multipleRoomsMultipleGuests', '{0}', '{1}'),
    },
    roomPicker: {
      __typename: 'RoomPicker',
      toolBarIconLabel: formatText('hotels.roomPicker.cancelButton.a11yLabel'),
      toolBarTitle: formatText('hotels.roomPicker.title'),
      addRoomText: formatText('hotels.roomPicker.addRoomButton.label'),
      doneLabel: doneLabel || formatText('hotels.roomPicker.doneButton.label'),
      roomAddedLiveAnnounce: formatText('hotels.roomPicker.roomAdded.liveAnnounce', '{0}'),
      room: {
        __typename: 'RoomField',
        roomTitleAriaLabel: formatText('hotels.roomPicker.room.a11yLabelRoom', '{0}', '{1}'),
        roomTitle: formatText('hotels.roomPicker.room.title', '{0}'),
        adultsStepInput: {
          __typename: 'StepInput',
          title: formatText('hotels.roomPicker.adults.text'),
          increaseText: formatText('hotels.roomPicker.increaseAdults.text', '{0}'),
          decreaseText: formatText('hotels.roomPicker.decreaseAdults.text', '{0}'),
          caption: null,
        },
        childrenStepInput: {
          __typename: 'StepInput',
          title: formatText('hotels.roomPicker.children.text'),
          increaseText: formatText('hotels.roomPicker.increaseChildren.text', '{0}'),
          decreaseText: formatText('hotels.roomPicker.decreaseChildren.text', '{0}'),
          caption: formatText('hotels.roomPicker.children.caption'),
        },
        removeRoomButtonLabel: formatText('hotels.roomPicker.removeRoomButton.label'),
        removeRoomButtonAriaLabel: formatText('hotels.roomPicker.removeRoomButton.a11yLabel', '{0}', '{1}'),
        roomRemovedLiveAnnounce: formatText('hotels.roomPicker.roomRemoved.liveAnnounce', '{0}'),
        childrenAges: {
          __typename: 'ChildrenAges',
          selectChildAgeLabel: formatText('hotels.roomPicker.childAgeInput.label', '{0}'),
          youngestChildAgeText: formatText('hotels.roomPicker.youngestChildAge.text'),
          childAgeUpdatedLiveAnnounce: formatText('hotels.roomPicker.childUpdated.liveAnnounce', '{0}', '{1}'),
        },
      },
    },
  };

  return <TravelersField fieldName="travelers" data={travelersData} closeOnSubmit={closeOnSubmit} trigger={trigger} />;
};

export const SharedUiSubmitButtonWrapper: React.FC<{ text: string }> = ({ text }) => {
  const submitButtonData: SubmitButtonData = {
    __typename: 'LodgingFormSubmitButton',
    label: text,
  };

  return <SubmitButton fieldName="submit" data={submitButtonData} />;
};

export const SharedUiTravelersLinkWrapper: React.FC<{ currentHotel?: CurrentHotelStore; handleChange: (a) => void }> = inject(
  'currentHotel'
)(({ currentHotel, handleChange }) => {
  const { formatText } = useLocalization();

  const { hotelId, searchCriteria } = currentHotel;
  const { context } = useBexApi();

  const inputs: FormInputs = React.useMemo(
    () => ({
      propertyId: String(hotelId),
      dateRange: searchCriteria ? getDateRangeInput(searchCriteria) : null,
      roomInput: searchCriteria ? getRooms(searchCriteria) : [],
    }),
    [hotelId, searchCriteria?.startDate, searchCriteria?.endDate, searchCriteria?.rooms]
  );

  const variable: FormQueryVariables = {
    ...((inputs || {}) as FormInputs),
    context,
  };

  const doneLabel = formatText('hotels.infosite.searchForm.submit.label');

  return (
    <ActionProviderDatedMobile onChange={handleChange}>
      <FormInputsProvider value={variable}>
        <SharedUiTravelersWrapper doneLabel={doneLabel} trigger={TravelersFieldTriggerType.LINK} closeOnSubmit={false} />
      </FormInputsProvider>
    </ActionProviderDatedMobile>
  );
});

SharedUiTravelersLinkWrapper.displayName = 'SharedUiTravelersLinkWrapper';
